import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Envelope, PaperPlaneTilt, X, FileArrowUp } from '@phosphor-icons/react';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';

const MAX_ATTACH_BYTES = 10 * 1024 * 1024;
const ACCEPTED = '.pdf,.xlsx,.xls,.png,.jpg,.jpeg';

/**
 * Super-admin broadcast composer.
 *
 * Structured form on the left (Category / Headline / Subtitle / CTA /
 * Highlights or free-form body when template = Blank) + a live preview on
 * the right. Recipient toggles pick "all tenant admins" and/or "all tenant
 * users". Attachments (PDF/XLSX/PNG/JPG, 10 MB total) attach to every
 * outgoing email. Sends run in a FastAPI background task; we poll
 * /broadcasts/{id} for progress until status === 'complete'.
 */
export default function AdminBroadcastsPage() {
  const { apiCall } = useAuth();

  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('platform_update');
  const [subject, setSubject] = useState('');
  const [headline, setHeadline] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [ctaLabel, setCtaLabel] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [highlights, setHighlights] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [toTenantAdmins, setToTenantAdmins] = useState(true);
  const [toTenantUsers, setToTenantUsers] = useState(false);
  const [files, setFiles] = useState([]);
  const [previewHtml, setPreviewHtml] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(null); // { total, sent, failed, status }
  const [error, setError] = useState('');

  const structured = useMemo(
    () => templates.find((t) => t.id === templateId)?.structured ?? true,
    [templates, templateId],
  );

  useEffect(() => {
    apiCall('get', '/broadcasts/templates').then((r) => setTemplates(r?.data?.templates ?? [])).catch(() => {});
  }, [apiCall]);

  // Debounced live preview.
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const res = await apiCall('post', '/broadcasts/preview', {
          template_id: templateId, subject, headline, subtitle,
          cta_label: ctaLabel, cta_url: ctaUrl, highlights, body_html: bodyHtml,
        });
        setPreviewHtml(res?.data?.html || '');
      } catch { /* preview is best-effort */ }
    }, 250);
    return () => clearTimeout(t);
  }, [apiCall, templateId, subject, headline, subtitle, ctaLabel, ctaUrl, highlights, bodyHtml]);

  const totalBytes = files.reduce((s, f) => s + f.size, 0);
  const overCap = totalBytes > MAX_ATTACH_BYTES;

  const handlePickFiles = (fileList) => {
    const next = [...files];
    for (const f of Array.from(fileList || [])) {
      if (!ACCEPTED.split(',').some((ext) => f.name.toLowerCase().endsWith(ext))) continue;
      next.push(f);
    }
    setFiles(next);
  };

  const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));

  const canSend = subject.trim() && (toTenantAdmins || toTenantUsers) && !overCap && !sending;

  const send = async () => {
    setConfirmOpen(false);
    setSending(true);
    setError('');
    const fd = new FormData();
    fd.append('subject', subject);
    fd.append('template_id', templateId);
    fd.append('to_tenant_admins', String(toTenantAdmins));
    fd.append('to_tenant_users', String(toTenantUsers));
    fd.append('headline', headline);
    fd.append('subtitle', subtitle);
    fd.append('cta_label', ctaLabel);
    fd.append('cta_url', ctaUrl);
    fd.append('highlights', highlights);
    fd.append('body_html', bodyHtml);
    files.forEach((f) => fd.append('files', f));
    try {
      const res = await apiCall('post', '/broadcasts/send', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const id = res?.data?.campaign_id;
      setProgress({ total: res?.data?.recipient_count || 0, sent: 0, failed: 0, status: 'sending' });
      // Poll every 2s until complete.
      const poll = async () => {
        try {
          const p = await apiCall('get', `/broadcasts/${id}`);
          const d = p?.data;
          setProgress({ total: d.progress?.total, sent: d.progress?.sent, failed: d.progress?.failed, status: d.status });
          if (d.status !== 'complete') setTimeout(poll, 2000);
        } catch { setTimeout(poll, 4000); }
      };
      poll();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto animate-fadeIn" data-testid="admin-broadcasts-page">
      <div className="flex items-center gap-2 mb-6">
        <Envelope size={22} weight="fill" className="text-primary" />
        <h1 className="text-2xl font-bold">Broadcast Emails</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Composer */}
        <Card className="border-0 shadow-card"><CardContent className="p-5 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Recipients</label>
            <div className="flex gap-4 mt-2 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={toTenantAdmins} onChange={(e) => setToTenantAdmins(e.target.checked)} data-testid="broadcast-recipient-tenants-toggle" />
                All tenant admins
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={toTenantUsers} onChange={(e) => setToTenantUsers(e.target.checked)} data-testid="broadcast-recipient-users-toggle" />
                All tenant users
              </label>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Template</label>
            <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} className="w-full mt-2 px-3 py-2 text-sm rounded-md bg-background border border-border" data-testid="broadcast-template-select">
              {templates.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Subject</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Sharper satellite imagery is arriving soon" className="mt-2" data-testid="broadcast-subject-input" />
          </div>

          {structured ? (
            <>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Headline</label>
                <Input value={headline} onChange={(e) => setHeadline(e.target.value)} className="mt-2" data-testid="broadcast-headline-input" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Subtitle</label>
                <Input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} className="mt-2" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">CTA label</label>
                  <Input value={ctaLabel} onChange={(e) => setCtaLabel(e.target.value)} className="mt-2" placeholder="LIVE FROM 7 SEPTEMBER" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">CTA URL</label>
                  <Input value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)} className="mt-2" placeholder="https://roofmate.app" />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Highlights (one per line, max 5)</label>
                <Textarea value={highlights} onChange={(e) => setHighlights(e.target.value)} rows={4} className="mt-2 font-mono text-xs" placeholder="Clearer imagery across all areas&#10;Improved mapping detail&#10;A better viewing experience" />
              </div>
            </>
          ) : (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Body (HTML allowed)</label>
              <Textarea value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)} rows={10} className="mt-2 font-mono text-xs" />
            </div>
          )}

          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Attachments (PDF, XLSX, PNG, JPG · 10 MB total)</label>
            <label className="mt-2 flex items-center justify-center gap-2 border-2 border-dashed border-border rounded-md py-4 cursor-pointer hover:bg-muted/40 text-xs" data-testid="broadcast-attachment-drop">
              <FileArrowUp size={16} />
              <span>Click to add files</span>
              <input type="file" accept={ACCEPTED} multiple hidden onChange={(e) => handlePickFiles(e.target.files)} />
            </label>
            {files.length > 0 && (
              <ul className="mt-2 space-y-1">
                {files.map((f, i) => (
                  <li key={i} className="flex items-center justify-between text-xs bg-muted/40 px-2 py-1 rounded">
                    <span className="truncate">{f.name} · {(f.size / 1024).toFixed(0)} KB</span>
                    <button onClick={() => removeFile(i)} className="ml-2 opacity-70 hover:opacity-100"><X size={12} /></button>
                  </li>
                ))}
              </ul>
            )}
            {overCap && <div className="text-xs text-rose-600 mt-1">Over 10 MB. Remove some files.</div>}
          </div>

          {error && <div className="text-xs text-rose-600" data-testid="broadcast-error">{error}</div>}
          {progress && (
            <div className="text-xs bg-primary/5 border border-primary/20 rounded p-3" data-testid="broadcast-progress">
              <div className="font-semibold">Campaign {progress.status === 'complete' ? 'complete' : 'sending…'}</div>
              <div className="opacity-80 mt-1">Sent {progress.sent} / {progress.total} · Failed {progress.failed}</div>
            </div>
          )}

          <Button onClick={() => setConfirmOpen(true)} disabled={!canSend} className="w-full" data-testid="broadcast-send-btn">
            <PaperPlaneTilt size={14} weight="bold" className="mr-2" />
            {sending ? 'Sending…' : 'Send campaign'}
          </Button>
          <div className="text-[11px] text-muted-foreground text-center">Sent from noreply@roofmate.app</div>
        </CardContent></Card>

        {/* Live preview */}
        <Card className="border-0 shadow-card"><CardContent className="p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold px-2 py-2">Live preview</div>
          <iframe title="preview" srcDoc={previewHtml} className="w-full h-[700px] bg-white rounded-md border border-border" data-testid="broadcast-preview-iframe" />
        </CardContent></Card>
      </div>

      <ConfirmDeleteDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Send this campaign?"
        description={`Emails will be sent from noreply@roofmate.app to every ${toTenantAdmins ? 'tenant admin' : ''}${toTenantAdmins && toTenantUsers ? ' + ' : ''}${toTenantUsers ? 'tenant user' : ''}. This can't be undone.`}
        onConfirm={send}
        destructiveLabel="Send now"
      />
    </div>
  );
}
